import { useState, useEffect, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import * as Sentry from "@sentry/react";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Package,
  Wand2,
  Camera,
  FlaskConical,
  Copy,
  CheckCheck,
  ChevronRight,
  AlertTriangle,
  Loader2,
} from "lucide-react";

import { cn } from "@/lib/utils";
import { WizardPreview } from "@/components/app/WizardPreview";

interface ProductOption {
  id: string;
  name: string;
  product_params: Record<string, any> | null;
}

interface ConfigOption {
  id: string;
  name: string;
  config: any;
  region_key: string | null;
}

/**
 * Look up a selected value's promptFragment from a fieldDefinitions array.
 * No fallback values: if fragment is missing, return empty string.
 */
function resolveFragment(fieldDefs: any[], key: string, value: string): string {
  const def = fieldDefs?.find((fd: any) => fd.key === key);
  if (!def?.options) return "";
  const opt = def.options.find((o: any) => o.key === value);
  return opt?.promptFragment || "";
}

function assembleProductDetails(
  productParams: Record<string, any>,
  productConfig: any | null
): string {
  const details: string[] = [];
  const fieldDefs = productConfig?.sections
    ?.flatMap((s: any) => s.fields || [])
    ?.filter((f: any) => f.type === "select" && f.options) || [];

  if (productParams.brand || productParams.model) {
    details.push(`Brand/Model: ${[productParams.brand, productParams.model].filter(Boolean).join(" ")}`);
  }
  if (productParams.width_cm || productParams.height_cm) {
    details.push(`Dimensions: ${productParams.width_cm || "?"}cm W × ${productParams.height_cm || "?"}cm H × ${productParams.depth_cm || "?"}cm D`);
  }
  if (productParams.primary_color) {
    details.push(`Color: ${productParams.primary_color}${productParams.secondary_color ? `, accent: ${productParams.secondary_color}` : ""}`);
  }
  if (productParams.finish) {
    details.push(`Finish: ${resolveFragment(fieldDefs, "finish", productParams.finish)}`);
  }
  if (productParams.texture_notes) details.push(`Texture: ${productParams.texture_notes}`);
  if (productParams.mounting_type) {
    details.push(`Mounting: ${resolveFragment(fieldDefs, "mounting_type", productParams.mounting_type)}`);
  }
  if (productParams.ideal_height) details.push(`Ideal height: ${productParams.ideal_height}`);
  if (productParams.ideal_position) details.push(`Position: ${productParams.ideal_position}`);
  if (productParams.distinguishing_features?.length) {
    details.push(`Key features: ${productParams.distinguishing_features.join("; ")}`);
  }
  if (productParams.avoid_positions?.length) {
    details.push(`Avoid: ${productParams.avoid_positions.join("; ")}`);
  }
  return details.join("\n");
}

function assemblePhotographerDirection(photographerConfig: any | null): string {
  if (!photographerConfig) return "";
  const settings = photographerConfig.settings || {};
  // No fallbacks — config MUST contain fieldDefinitions with promptFragment values
  const fieldDefs = photographerConfig.fieldDefinitions || [];
  const fragments: string[] = [];

  for (const [key, value] of Object.entries(settings)) {
    if (!value) continue;
    const fragment = resolveFragment(fieldDefs, key, value as string);
    if (fragment) fragments.push(fragment);
  }

  return fragments.join(". ") + (fragments.length ? "." : "");
}

/** Assemble prompt — mirrors edge function logic exactly */
function assemblePrompt(
  briefConfig: any,
  answers: Record<string, string>,
  productParams: Record<string, any> | null,
  productConfig: any | null,
  photographerConfig: any | null
): string {
  let result: string = briefConfig.promptTemplate || "";

  // {{_region_context}}
  result = result.replace(/\{\{_region_context\}\}/g, briefConfig.regionPromptFragment || "");

  // {{_product_description}}
  result = result.replace(/\{\{_product_description\}\}/g, productParams?.product_description || "");

  // {{_reference_image_list}}
  result = result.replace(
    /\{\{_reference_image_list\}\}/g,
    productParams?.image_slots_description || "product reference images showing various angles"
  );

  // {{_product_details}}
  if (productParams) {
    result = result.replace(/\{\{_product_details\}\}/g, assembleProductDetails(productParams, productConfig));
  } else {
    result = result.replace(/\{\{_product_details\}\}/g, "");
  }

  // {{_photographer_direction}}
  result = result.replace(/\{\{_photographer_direction\}\}/g, assemblePhotographerDirection(photographerConfig));

  // Replace all {{field_key}} placeholders using promptFragments + answers
  const fragments = briefConfig.promptFragments || {};
  const placeholders = result.match(/\{\{(\w+)\}\}/g) || [];
  for (const placeholder of placeholders) {
    const fieldKey = placeholder.replace(/\{\{|\}\}/g, "");
    const answerValue = answers[fieldKey] || "";
    const fragmentMap = fragments[fieldKey];
    if (fragmentMap && answerValue && fragmentMap[answerValue]) {
      result = result.replace(placeholder, fragmentMap[answerValue]);
    } else if (answerValue) {
      result = result.replace(placeholder, answerValue);
    } else {
      result = result.replace(placeholder, "");
    }
  }

  return result
    .split("\n")
    .map((line) => line.trimEnd())
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

const REGION_FLAGS: Record<string, string> = {
  nordics_no: "🇳🇴", nordics_fi: "🇫🇮", nordics_se: "🇸🇪", nordics_dk: "🇩🇰",
  dach_de: "🇩🇪", dach_at: "🇦🇹", dach_ch: "🇨🇭",
  benelux_nl: "🇳🇱", benelux_be: "🇧🇪",
  southern_fr: "🇫🇷", southern_it: "🇮🇹", southern_es: "🇪🇸",
  uk_gb: "🇬🇧",
};

export default function PromptPlayground() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Data
  const [products, setProducts] = useState<ProductOption[]>([]);
  const [briefConfigs, setBriefConfigs] = useState<ConfigOption[]>([]);
  const [photographerConfigs, setPhotographerConfigs] = useState<ConfigOption[]>([]);
  const [productConfigs, setProductConfigs] = useState<ConfigOption[]>([]);

  // Selections
  const [selectedProductId, setSelectedProductId] = useState<string>("");
  const [selectedBriefId, setSelectedBriefId] = useState<string>("");
  const [selectedPhotographerId, setSelectedPhotographerId] = useState<string>("");
  const [selectedProductConfigId, setSelectedProductConfigId] = useState<string>("");
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [showPrompt, setShowPrompt] = useState(false);
  const [copied, setCopied] = useState(false);

  const selectedProduct = products.find((p) => p.id === selectedProductId);
  const selectedBrief = briefConfigs.find((b) => b.id === selectedBriefId);
  const selectedPhotographer = photographerConfigs.find((p) => p.id === selectedPhotographerId);
  const selectedProductConfig = productConfigs.find((p) => p.id === selectedProductConfigId);

  // Load data
  useEffect(() => {
    if (!user) return;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [productsRes, briefsRes, photographersRes, productConfigsRes] = await Promise.all([
          supabase.from("products").select("id, name, product_params").order("name"),
          supabase.from("wizard_configs").select("id, name, config, region_key").eq("type", "brief").order("updated_at", { ascending: false }),
          supabase.from("wizard_configs").select("id, name, config, region_key").eq("type", "photographer").order("updated_at", { ascending: false }),
          supabase.from("wizard_configs").select("id, name, config, region_key").eq("type", "product").order("updated_at", { ascending: false }),
        ]);
        const failed = [productsRes, briefsRes, photographersRes, productConfigsRes].filter(r => r.error);
        if (failed.length > 0) {
          for (const res of failed) {
            console.error("Failed to load playground data:", res.error);
            Sentry.captureException(res.error);
          }
          setError(`Failed to load data. ${failed.length} query(s) failed.`);
        }
        if (productsRes.data) setProducts(productsRes.data as ProductOption[]);
        if (briefsRes.data) setBriefConfigs(briefsRes.data as ConfigOption[]);
        if (photographersRes.data) {
          setPhotographerConfigs(photographersRes.data as ConfigOption[]);
          if (photographersRes.data.length > 0) setSelectedPhotographerId(photographersRes.data[0].id);
        }
        if (productConfigsRes.data) {
          setProductConfigs(productConfigsRes.data as ConfigOption[]);
          if (productConfigsRes.data.length > 0) setSelectedProductConfigId(productConfigsRes.data[0].id);
        }
      } catch (err) {
        console.error("Unexpected error loading playground data:", err);
        Sentry.captureException(err);
        setError("An unexpected error occurred. Try refreshing the page.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user]);

  // Reset answers when brief changes
  useEffect(() => {
    setAnswers({});
    setShowPrompt(false);
  }, [selectedBriefId]);

  // Assembled prompt
  const assembledPrompt = useMemo(() => {
    if (!selectedBrief) return "";
    return assemblePrompt(
      selectedBrief.config,
      answers,
      selectedProduct?.product_params || null,
      selectedProductConfig?.config || null,
      selectedPhotographer?.config || null
    );
  }, [selectedBrief, answers, selectedProduct, selectedPhotographer, selectedProductConfig]);

  // Product params diagnostic
  const productDiagnostics = useMemo(() => {
    if (!selectedProduct?.product_params) return [];
    const pp = selectedProduct.product_params;
    const items: { label: string; value: string; warn?: boolean }[] = [];
    if (pp.brand) items.push({ label: "Brand", value: pp.brand });
    if (pp.model) items.push({ label: "Model", value: pp.model });
    if (pp.mounting_type) items.push({ label: "Mounting", value: pp.mounting_type });
    if (pp.ideal_height) items.push({ label: "Ideal Height", value: pp.ideal_height });
    if (pp.width_cm) items.push({ label: "Width", value: `${pp.width_cm} cm` });
    if (pp.height_cm) items.push({ label: "Height", value: `${pp.height_cm} cm` });
    if (pp.product_description) items.push({ label: "Description", value: pp.product_description });
    return items;
  }, [selectedProduct]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(assembledPrompt);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy to clipboard:", err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const readyToAssemble = !!selectedBriefId;

  return (
    <div className="p-8 h-[calc(100vh-2rem)] flex flex-col">
      {error && (
        <div className="mb-4 bg-destructive/10 border border-destructive/20 rounded-lg p-3 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2 text-sm text-destructive">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            {error}
          </div>
          <button onClick={() => setError(null)} className="text-destructive/60 hover:text-destructive text-sm">Dismiss</button>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center gap-3 mb-4 shrink-0">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
          <FlaskConical className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h1 className="text-xl font-semibold">Prompt Playground</h1>
          <p className="text-sm text-muted-foreground">
            Select a product, brief, &amp; photographer config — inspect the assembled prompt
          </p>
        </div>
      </div>

      {/* Selection bar */}
      <div className="flex items-end gap-3 mb-4 shrink-0 flex-wrap">
        <div className="flex-1 min-w-[160px]">
          <Label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">
            <Package className="w-3 h-3 inline mr-1" />
            Product
          </Label>
          <Select value={selectedProductId} onValueChange={setSelectedProductId}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Choose a product..." />
            </SelectTrigger>
            <SelectContent>
              {products.map((p) => (
                <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
              ))}
              {products.length === 0 && (
                <div className="px-2 py-1.5 text-xs text-muted-foreground">No products found</div>
              )}
            </SelectContent>
          </Select>
        </div>

        <ChevronRight className="w-4 h-4 text-muted-foreground mb-2.5 shrink-0" />

        <div className="flex-1 min-w-[160px]">
          <Label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">
            <Wand2 className="w-3 h-3 inline mr-1" />
            Brief Config
          </Label>
          <Select value={selectedBriefId} onValueChange={setSelectedBriefId}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Choose a brief..." />
            </SelectTrigger>
            <SelectContent>
              {briefConfigs.map((b) => (
                <SelectItem key={b.id} value={b.id}>
                  <span className="mr-1.5">
                    {b.region_key ? REGION_FLAGS[b.region_key] || "🌍" : "🌐"}
                  </span>
                  {b.name}
                </SelectItem>
              ))}
              {briefConfigs.length === 0 && (
                <div className="px-2 py-1.5 text-xs text-muted-foreground">No brief configs found</div>
              )}
            </SelectContent>
          </Select>
        </div>

        <ChevronRight className="w-4 h-4 text-muted-foreground mb-2.5 shrink-0" />

        <div className="flex-1 min-w-[160px]">
          <Label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">
            <Camera className="w-3 h-3 inline mr-1" />
            Photographer
          </Label>
          <Select value={selectedPhotographerId} onValueChange={setSelectedPhotographerId}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Choose a photographer..." />
            </SelectTrigger>
            <SelectContent>
              {photographerConfigs.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {(p.config as any)?.emoji || "📷"} {p.name}
                </SelectItem>
              ))}
              {photographerConfigs.length === 0 && (
                <div className="px-2 py-1.5 text-xs text-muted-foreground">No photographer configs found</div>
              )}
            </SelectContent>
          </Select>
        </div>

        <Button
          size="sm"
          onClick={() => setShowPrompt(true)}
          disabled={!readyToAssemble}
          className="mb-0.5 shrink-0"
        >
          <FlaskConical className="w-3.5 h-3.5 mr-1" />
          Assemble
        </Button>
      </div>

      {/* Product params diagnostic */}
      {selectedProduct && productDiagnostics.length > 0 && (
        <div className="mb-4 shrink-0">
          <Label className="text-xs text-muted-foreground uppercase tracking-wider mb-2 block">
            Product Params (from database)
          </Label>
          <div className="flex flex-wrap gap-2">
            {productDiagnostics.map((d, i) => (
              <Badge
                key={i}
                variant={d.warn ? "destructive" : "secondary"}
                className={cn("text-xs gap-1", d.warn && "bg-destructive/10 text-destructive border-destructive/30")}
              >
                {d.warn && <AlertTriangle className="w-3 h-3" />}
                <span className="font-medium">{d.label}:</span>
                <span className="font-normal">{d.value}</span>
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Photographer summary badge */}
      {selectedPhotographer && (
        <div className="mb-4 shrink-0">
          <Label className="text-xs text-muted-foreground uppercase tracking-wider mb-2 block">
            Photographer Settings
          </Label>
          <div className="flex flex-wrap gap-2">
            {Object.entries((selectedPhotographer.config as any)?.settings || {}).map(([key, value]) => (
              <Badge key={key} variant="outline" className="text-xs">
                <span className="font-medium">{key}:</span>
                <span className="font-normal ml-1">{value as string}</span>
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Main area: Wizard + Prompt */}
      <div className="flex-1 min-h-0 flex gap-4">
        {/* Wizard panel */}
        {selectedBrief ? (
          <div className="flex-1 min-w-0 rounded-xl border border-border overflow-hidden bg-card">
            <WizardPreview
              config={selectedBrief.config}
              mode="preview"
              initialAnswers={answers}
              onAnswerChange={(a) => setAnswers(a)}
              onSave={(a) => {
                setAnswers(a);
                setShowPrompt(true);
              }}
            />
          </div>
        ) : (
          <div className="flex-1 min-w-0 rounded-xl border border-border flex items-center justify-center bg-card">
            <div className="text-center text-muted-foreground">
              <Wand2 className="w-8 h-8 mx-auto mb-2 opacity-30" />
              <p className="text-sm">Select a brief config to see the wizard</p>
            </div>
          </div>
        )}

        {/* Prompt output panel */}
        <div className="w-[480px] shrink-0 rounded-xl border border-border bg-card flex flex-col overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border shrink-0">
            <div className="flex items-center gap-2">
              <FlaskConical className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium">Assembled Prompt</span>
            </div>
            {assembledPrompt && (
              <Button variant="ghost" size="sm" onClick={handleCopy}>
                {copied ? (
                  <CheckCheck className="w-3.5 h-3.5" />
                ) : (
                  <Copy className="w-3.5 h-3.5" />
                )}
              </Button>
            )}
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            {assembledPrompt ? (
              <pre className="font-mono text-xs leading-relaxed text-foreground whitespace-pre-wrap">
                {assembledPrompt}
              </pre>
            ) : (
              <div className="h-full flex items-center justify-center text-muted-foreground">
                <p className="text-sm text-center">
                  {readyToAssemble
                    ? "Fill in wizard steps, then click Assemble or navigate to the Prompt Result tab"
                    : "Select a product and brief config to get started"}
                </p>
              </div>
            )}
          </div>

          {/* Stats footer */}
          {assembledPrompt && (
            <div className="px-4 py-2 border-t border-border bg-muted/30 flex items-center gap-4 text-xs text-muted-foreground shrink-0">
              <span>{assembledPrompt.length} chars</span>
              <span>{assembledPrompt.split(/\s+/).length} words</span>
              <span>~{Math.ceil(assembledPrompt.length / 4)} tokens</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
